<script>
import FilterPanel from '@/components/common/FilterPanel.vue';
import FilterField from '@/components/common/FilterField.vue';
import MultiSelectFilter from '@/components/common/MultiSelectFilter.vue';
import DateRangeFilter from '@/components/common/DateRangeFilter.vue';
import SavedFilters from '@/components/common/SavedFilters.vue';
import { SAVED_FILTER_SCOPES } from '@/shared/services/saved-filter.service';

/**
 * Les critères de la liste des comptes.
 *
 * La rangée d'origine — recherche, date, téléphone — ne répondait pas aux
 * questions qu'on pose vraiment à cette liste: qui attend encore son lien
 * d'activation, qui n'a jamais saisi d'enquête, qui a été créé pendant telle
 * semaine. Les critères sont donc plus nombreux, mais rangés: la recherche
 * reste en vue, le reste s'ouvre dans un panneau où chaque champ est nommé.
 */
export default {
  name: 'UserFilters',
  components: {
    FilterPanel,
    FilterField,
    MultiSelectFilter,
    DateRangeFilter,
    SavedFilters,
  },
  props: {
    modelValue: { type: Object, required: true },
    /** [{ value, label }] — les rôles que le compte connecté peut filtrer. */
    roleOptions: { type: Array, default: () => [] },
    /** Nombre de critères actifs, pour la pastille du panneau. */
    activeCount: { type: Number, default: 0 },
    /** Les critères actifs, tels qu'on les enregistrerait. */
    criteria: { type: Object, default: () => ({}) },
    appliedFilterId: { type: String, default: null },
    expanded: { type: Boolean, default: false },
  },
  emits: [
    'update:modelValue',
    'update:expanded',
    'reset',
    'apply-saved',
    'saved-filters-loaded',
  ],
  data() {
    return {
      scope: SAVED_FILTER_SCOPES.USERS,
      presenceOptions: [
        { value: '', label: 'Peu importe' },
        { value: 'yes', label: 'Renseigné' },
        { value: 'no', label: 'Non renseigné' },
      ],
      submissionOptions: [
        { value: '', label: 'Peu importe' },
        { value: 'yes', label: 'Au moins une enquête' },
        { value: 'no', label: 'Aucune enquête' },
      ],
      accountStateOptions: [
        { value: '', label: 'Tous les états' },
        { value: 'active', label: 'Actif' },
        { value: 'pending', label: 'Activation en attente' },
      ],
    };
  },
  methods: {
    update(field, value) {
      this.$emit('update:modelValue', { ...this.modelValue, [field]: value });
    },
  },
};
</script>

<template>
  <FilterPanel
    :model-value="modelValue.searchQuery"
    :expanded="expanded"
    :active-count="activeCount"
    search-placeholder="Rechercher un nom, un email…"
    @update:model-value="update('searchQuery', $event)"
    @update:expanded="$emit('update:expanded', $event)"
    @reset="$emit('reset')"
  >
    <template #quick>
      <select
        class="list-select"
        :value="modelValue.sort"
        aria-label="Trier les utilisateurs"
        data-testid="users-sort"
        @change="update('sort', $event.target.value)"
      >
        <option value="recent">Tri : plus récents</option>
        <option value="idAsc">Tri : ID croissant</option>
        <option value="nameAsc">Tri : nom A→Z</option>
      </select>
    </template>

    <template #saved>
      <SavedFilters
        :scope="scope"
        :criteria="criteria"
        :applied-id="appliedFilterId"
        @apply="$emit('apply-saved', $event)"
        @loaded="$emit('saved-filters-loaded', $event)"
      />
    </template>

    <template #advanced>
      <FilterField label="Rôles">
        <MultiSelectFilter
          :model-value="modelValue.roles"
          :options="roleOptions"
          placeholder="Tous les rôles"
          @update:model-value="update('roles', $event)"
        />
      </FilterField>

      <FilterField label="Email">
        <input
          type="text"
          class="list-input"
          placeholder="Fragment d'adresse…"
          :value="modelValue.email"
          data-testid="users-email"
          @input="update('email', $event.target.value)"
        />
      </FilterField>

      <FilterField label="Téléphone">
        <input
          type="text"
          class="list-input"
          placeholder="Numéro ou fragment…"
          :value="modelValue.phone"
          data-testid="users-phone"
          @input="update('phone', $event.target.value)"
        />
      </FilterField>

      <FilterField label="Téléphone renseigné">
        <select
          class="list-select"
          :value="modelValue.hasPhone"
          data-testid="users-has-phone"
          @change="update('hasPhone', $event.target.value)"
        >
          <option
            v-for="option in presenceOptions"
            :key="option.value"
            :value="option.value"
          >
            {{ option.label }}
          </option>
        </select>
      </FilterField>

      <FilterField label="État du compte">
        <select
          class="list-select"
          :value="modelValue.accountState"
          data-testid="users-account-state"
          @change="update('accountState', $event.target.value)"
        >
          <option
            v-for="option in accountStateOptions"
            :key="option.value"
            :value="option.value"
          >
            {{ option.label }}
          </option>
        </select>
      </FilterField>

      <FilterField label="Enquêtes saisies">
        <select
          class="list-select"
          :value="modelValue.hasSubmissions"
          data-testid="users-has-submissions"
          @change="update('hasSubmissions', $event.target.value)"
        >
          <option
            v-for="option in submissionOptions"
            :key="option.value"
            :value="option.value"
          >
            {{ option.label }}
          </option>
        </select>
      </FilterField>

      <FilterField label="Date d'ajout" wide>
        <DateRangeFilter
          :model-value="modelValue.dateAdded"
          @update:model-value="update('dateAdded', $event)"
        />
      </FilterField>
    </template>
  </FilterPanel>
</template>
